import { useCallback, type AnchorHTMLAttributes, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface HashLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  /** Anchor id to scroll to (without the leading `#`). */
  to: string;
  /** Route to navigate to when the user is not already there. Defaults to `/`. */
  route?: string;
  children: ReactNode;
}

/**
 * Anchor link to an in-page section (e.g. `#contacto`) that works whether the
 * user is on the target route or somewhere else in the SPA.
 *
 * - Same route → just scrolls the section into view (no history push).
 * - Different route → pushes `route + #to` so the router renders the page
 *   first and `useHashScroll` then scrolls the section into view.
 *
 * Falls back to a regular `<a href>` so middle-click / cmd-click still opens
 * a new tab to the canonical URL.
 */
export function HashLink({ to, route = '/', onClick, children, ...rest }: HashLinkProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleClick = useCallback<NonNullable<HashLinkProps['onClick']>>(
    (event) => {
      onClick?.(event);
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      event.preventDefault();
      const onTargetRoute = pathname === route || (route === '/' && pathname === '');
      if (onTargetRoute) {
        const target = document.getElementById(to);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          if (window.history.replaceState) {
            window.history.replaceState(null, '', `${route}#${to}`);
          }
          return;
        }
      }
      navigate(`${route}#${to}`);
    },
    [navigate, onClick, pathname, route, to],
  );

  return (
    <a href={`${route}#${to}`} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}
